import { useEffect, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { KeysysToast, LoadingSpinner, ProtectedRoute } from "./component-library";
import { useProfile, useToasts } from "./contexts";
import {
    Header,
    Login,
    Dashboard,
    SessionTimeout,
    DashboardRoutes,
    PinRequiredModal,
    ImportTissueForm,
} from "./components";
import { useProfileQuery } from "./queries";
import { Claims, ClaimsForAssigningRoles, UserTypes } from "./models";
import { BarcodeDemo } from "./components/barcodes/BarcodeDemo";
import { TissueReportRoutes } from "./components/tissue-reports/TissueReportRoutes";

const Accounts = lazy(() => import("./components/accounts/Accounts"));
const AccountFeeForm = lazy(() => import("./components/accounts/AccountFeeForm"));
const AccountFeesUpload = lazy(() => import("./components/accounts/AccountFeesUpload"));
const AccountForm = lazy(() => import("./components/accounts/AccountForm"));
const AccountPhoneNumberForm = lazy(() => import("./components/accounts/AccountPhoneNumberForm"));
const AccountTypes = lazy(() => import("./components/accounts/AccountTypes"));
const AccountTypeForm = lazy(() => import("./components/accounts/AccountTypeForm"));
const AccountPhonesUpload = lazy(() => import("./components/accounts/AcountPhonesUpload"));
const AccountUnitForm = lazy(() => import("./components/accounts/AccountUnitForm"));
const AccountUnitsUpload = lazy(() => import("./components/accounts/AccountUnitsUpload"));
const ActiveReferrals = lazy(() => import("./components/referrals/ActiveReferrals"));
const AccountUpload = lazy(() => import("./components/accounts/AccountsUpload"));
const AppSettings = lazy(() => import("./components/app-settings/AppSettings"));
const CaseView = lazy(() => import("./components/cases/CaseView"));
const ChartReviewLogs = lazy(() => import("./components/donors/ChartReviewLogs"));
const CreateInvoiceForm = lazy(() => import("./components/invoices/CreateInvoiceForm"));
const DonorLetters = lazy(() => import("./components/donor-letters/DonorLetters"));
const FailedRecoverySyncs = lazy(() => import("./components/failed-recovery-syncs/FailedRecoverySyncs"));
const ForgotPassword = lazy(() => import("./components/login/ForgotPassword"));
const FormSelectCategories = lazy(() => import("./components/form-select-categories/FormSelectCategories"));
const FormSelectCategoryForm = lazy(() => import("./components/form-select-categories/FormSelectCategoryForm"));
const FormDropdowns = lazy(() => import("./components/form-dropdowns/FormDropdowns"));
const FormDropdownForm = lazy(() => import("./components/form-dropdowns/FormDropdownForm"));
const FormDropdownUpload = lazy(() => import("./components/form-dropdowns/UploadFormDropdowns"));
const InvoiceReports = lazy(() => import("./components/invoice-reports/InvoiceReports"));
const InvoiceView = lazy(() => import("./components/invoices/InvoiceView"));
const LabKeySettings = lazy(() => import("./components/labkey/LabKeySettings"));
const Mfa = lazy(() => import("./components/mfa/Mfa"));
const MfaSetup = lazy(() => import("./components/mfa/MfaSetup"));
const MySurgeons = lazy(() => import("./components/surgery-scheduler-surgeons/MySurgeons"));
const NotificationConfigs = lazy(() => import("./components/notifications/NotificationConfigs"));
const NotificationConfigForm = lazy(() => import("./components/notifications/NotificationConfigForm"));
const Offices = lazy(() => import("./components/offices/Offices"));
const OfficeForm = lazy(() => import("./components/offices/OfficeForm"));
const Countries = lazy(() => import("./components/countries/Countries"));
const CountryForm = lazy(() => import("./components/countries/CountryForm"));
const OrderView = lazy(() => import("./components/orders/OrderView"));
const People = lazy(() => import("./components/people/People"));
const PeopleUpload = lazy(() => import("./components/people/PeopleUpload"));
const PersonForm = lazy(() => import("./components/people/PersonForm"));
const PersonTypes = lazy(() => import("./components/people/PersonTypes"));
const PersonTypeForm = lazy(() => import("./components/people/PersonTypeForm"));
const Profile = lazy(() => import("./components/profile/Profile"));
const QuickbooksView = lazy(() => import("./components/quickbooks/QuickbooksView"));
const ResetPassword = lazy(() => import("./components/login/ResetPassword"));
const Roles = lazy(() => import("./components/roles/Roles"));
const Search = lazy(() => import("./components/search/Search"));
const ShippingMethods = lazy(() => import("./components/shipping-methods/ShippingMethods"));
const ShippingMethodForm = lazy(() => import("./components/shipping-methods/ShippingMethodForm"));
const ShipmentView = lazy(() => import("./components/shipments/ShipmentView"));
const StandardFeeForm = lazy(() => import("./components/fees/StandardFeeForm"));
const StandardFees = lazy(() => import("./components/fees/StandardFees"));
const SurgeryCalendar = lazy(() => import("./components/surgery-calendar/SurgeryCalendar"));
const SurgeryView = lazy(() => import("./components/surgeries/SurgeryView"));
const SurgerySchedulerSurgeons = lazy(() => import("./components/surgery-scheduler-surgeons/SurgerySchedulerSurgeons"));
const Tenants = lazy(() => import("./components/tenants/Tenants"));
const TissueOfferLog = lazy(() => import("./components/tissue-offers/TissueOfferLog"));
const TissueTypes = lazy(() => import("./components/tissue-types/TissueTypes"));
const TissueView = lazy(() => import("./components/tissues/TissueView"));
const Users = lazy(() => import("./components/users/Users"));
const UserForm = lazy(() => import("./components/users/UserForm"));

export function App() {
    const { activeToasts } = useToasts();
    const location = useLocation();
    const { impersonator } = useProfile();
    const {
        logImpersonateMutation: { mutate: logImpersonation },
    } = useProfileQuery();

    useEffect(() => {
        if (impersonator) {
            const locationSliced = location.pathname.slice(location.pathname.lastIndexOf("/") + 1);
            logImpersonation(`Accessed ${locationSliced === "" ? "USERS" : locationSliced.toUpperCase()} Page`);
        }
    }, [impersonator, location.pathname, logImpersonation]);

    return (
        <>
            {!!activeToasts.length &&
                activeToasts.map((toast) => <KeysysToast key={`toast-${toast.name}`} {...toast} />)}
            <SessionTimeout />
            <Header />
            <PinRequiredModal />
            <main>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/mfa" element={<Mfa />} />
                        <Route path="/mfasetup" element={<MfaSetup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route
                            path="/app-settings"
                            element={
                                <ProtectedRoute
                                    anyClaim={[Claims.EditAnyTenantAppSettings, Claims.EditCurrentTenantAppSettings]}
                                >
                                    <AppSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/roles"
                            element={
                                <ProtectedRoute anyClaim={ClaimsForAssigningRoles}>
                                    <Roles />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/tenants"
                            element={
                                <ProtectedRoute anyClaim={[Claims.ViewAnyTenant, Claims.ViewChildTenant]}>
                                    <Tenants />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />

                        <Route element={<ProtectedRoute anyClaim={[Claims.ViewUser, Claims.ViewUserGlobal]} />}>
                            <Route path="/users/:userId" element={<UserForm />} />
                            <Route path="/users" element={<Users />} />
                        </Route>

                        <Route element={<ProtectedRoute anyClaim={[Claims.EditNotificationConfig]} />}>
                            <Route path="/notification-settings/:configId" element={<NotificationConfigForm />} />
                            <Route path="/notification-settings" element={<NotificationConfigs />} />
                        </Route>

                        <Route
                            path="/tissue-offer-log"
                            element={
                                <ProtectedRoute claim={Claims.OfferTissue}>
                                    <TissueOfferLog />
                                </ProtectedRoute>
                            }
                        />

                        <Route element={<ProtectedRoute isAuthenticated />}>
                            <Route
                                path="/form-select-categories/:formSelectCategoryId"
                                element={<FormSelectCategoryForm />}
                            />
                            <Route path="/form-select-categories" element={<FormSelectCategories />} />
                        </Route>

                        <Route element={<ProtectedRoute claim={Claims.ViewFormSelect} />}>
                            <Route path="/form-dropdowns/:formSelectId" element={<FormDropdownForm />} />
                            <Route path="/form-dropdowns" element={<FormDropdowns />} />
                        </Route>

                        <Route
                            element={
                                <ProtectedRoute claim={Claims.UploadFormSelect}>
                                    <FormDropdownUpload />
                                </ProtectedRoute>
                            }
                            path="/form-dropdowns/upload"
                        />

                        <Route element={<ProtectedRoute claim={Claims.ViewAccount} />}>
                            <Route path="/accounts" element={<Accounts />} />
                            <Route path="/accounts/:accountId" element={<AccountForm />} />
                            <Route path="/accounts/:accountId/units/:accountUnitId" element={<AccountUnitForm />} />
                            <Route path="/accounts/:accountId/fees/:accountFeeId" element={<AccountFeeForm />} />
                            <Route
                                path="/accounts/:accountId/phone-numbers/:phoneNumberId"
                                element={<AccountPhoneNumberForm />}
                            />
                            <Route path="account-types" element={<AccountTypes />} />
                            <Route path="/account-types/:accountTypeId" element={<AccountTypeForm />} />
                            <Route path="/offices" element={<Offices />} />
                            <Route path="/offices/:officeId" element={<OfficeForm />} />
                        </Route>

                        <Route element={<ProtectedRoute claim={Claims.UploadAccount} />}>
                            <Route path="/accounts/upload" element={<AccountUpload />} />
                            <Route path="/accounts/phones/upload" element={<AccountPhonesUpload />} />
                            <Route path="/accounts/units/upload" element={<AccountUnitsUpload />} />
                            <Route path="/accounts/fees/upload" element={<AccountFeesUpload />} />
                        </Route>

                        <Route element={<ProtectedRoute claim={Claims.ManageCountries} />}>
                            <Route path="/countries" element={<Countries />} />
                            <Route path="/countries/:countryId" element={<CountryForm />} />
                        </Route>

                        <Route element={<ProtectedRoute claim={Claims.ManageStandardFees} />}>
                            <Route path="/standard-fees/:standardFeeId" element={<StandardFeeForm />} />
                            <Route path="/standard-fees" element={<StandardFees />} />
                        </Route>

                        <Route element={<ProtectedRoute role={UserTypes.GlobalAdmin} />}>
                            <Route path="/tissue-types" element={<TissueTypes />} />
                        </Route>

                        <Route
                            path="/people"
                            element={
                                <ProtectedRoute claim={Claims.ViewPerson}>
                                    <People />
                                </ProtectedRoute>
                            }
                        />

                        <Route element={<ProtectedRoute claim={Claims.CreatePerson} />}>
                            <Route path="/people/upload" element={<PeopleUpload />} />
                        </Route>

                        <Route
                            path="/people/:personId"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    {/* surgeons and surgery schedulers can view */}
                                    <PersonForm />
                                </ProtectedRoute>
                            }
                        />

                        <Route element={<ProtectedRoute claim={Claims.ViewPersonType} />}>
                            <Route path="/person-types" element={<PersonTypes />} />
                            <Route path="/person-types/:personTypeId" element={<PersonTypeForm />} />
                        </Route>

                        <Route element={<ProtectedRoute claim={Claims.ManageShippingMethod} />}>
                            <Route path="/shipping-methods" element={<ShippingMethods />} />
                            <Route path="/shipping-methods/:shippingMethodId" element={<ShippingMethodForm />} />
                        </Route>

                        <Route
                            path="/surgery-calendar"
                            element={
                                <ProtectedRoute claim={Claims.ViewSurgery} isExternal>
                                    <SurgeryCalendar />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/create-invoice"
                            element={
                                <ProtectedRoute claim={Claims.CreateInvoice}>
                                    <CreateInvoiceForm />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="/invoices" element={<Navigate to="/search?tab=invoices" />} />

                        <Route path="/orders" element={<Navigate to="/search?tab=orders" />} />

                        <Route path="/tissues" element={<Navigate to="/search?tab=tissues" />} />

                        <Route path="/cases" element={<Navigate to="/search?tab=cases" />} />

                        <Route path="dashboard/*" element={<DashboardRoutes />} />

                        <Route
                            path="search"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <Search />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/cases"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <CaseView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/cases/:caseId/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <CaseView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/tissues/:tissueId/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <TissueView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/tissues"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <TissueView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/territory/:territoryId/tissues/:tissueId/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <TissueView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/territory/:territoryId/tissues"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <TissueView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/orders/:orderId/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <OrderView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/orders"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <OrderView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/shipments/:shipmentId/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <ShipmentView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/shipments"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <ShipmentView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/surgeries/:surgeryId/*"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <SurgeryView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/surgeries"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <SurgeryView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/:listDate/surgeries"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <SurgeryView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/:listDate/surgeries/:surgeryId/*"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <SurgeryView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/invoices"
                            element={
                                <ProtectedRoute claim={Claims.ViewInvoice}>
                                    <InvoiceView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="lists/:listSlug/invoices/:invoiceId/*"
                            element={
                                <ProtectedRoute claim={Claims.ViewInvoice}>
                                    <InvoiceView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="active-referrals"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <ActiveReferrals />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="failed-recovery-syncs"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <FailedRecoverySyncs />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="surgery-scheduler-surgeons"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <SurgerySchedulerSurgeons />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="qbo/*"
                            element={
                                <ProtectedRoute claim={Claims.ViewInvoice}>
                                    <QuickbooksView />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/barcode-demo"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <BarcodeDemo />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/import-tissue"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <ImportTissueForm />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/my-surgeons"
                            element={
                                <ProtectedRoute isAuthenticated isExternal>
                                    <MySurgeons />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/chart-review-logs"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <ChartReviewLogs />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/tissue-reports/*"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <TissueReportRoutes />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/invoice-reports"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <InvoiceReports />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/donor-letters"
                            element={
                                <ProtectedRoute isAuthenticated>
                                    <DonorLetters />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/labkey"
                            element={
                                <ProtectedRoute isAuthenticated role={UserTypes.GlobalAdmin}>
                                    <LabKeySettings />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            index
                            element={
                                <ProtectedRoute isAuthenticated isExternal redirectTo="/login">
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                </Suspense>
            </main>
        </>
    );
}
